/**
 * Drag and Drop Zone Canvas Rendering
 * 
 * Renders a circular drop zone in the center of the screen.
 * Visualizes drag state and provides visual feedback.
 */

import { drawText } from './text.js';
import { scaleFontSize } from './font-scale.js';
import type { GridState } from '../types/square.js';
import { dragDropContext, type DragDropContext } from '../systems/drag-drop-context.js';

export interface DragDropZoneState {
  isDragging: boolean;
  isOverZone: boolean;
  fileCount: number;
  cardCount: number;
}

/**
 * Draw drag and drop zone on canvas
 */
export function drawDragDropZone(
  ctx: CanvasRenderingContext2D,
  gridState: GridState,
  state: DragDropZoneState
): void {
  const centerX = gridState.width / 2;
  const centerY = gridState.height / 2;
  const radius = 120; // Base radius
  const activeRadius = state.isOverZone ? radius + 10 : radius; // Expand when dragging over

  // Draw outer glow when dragging over
  if (state.isOverZone) {
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = 'rgba(100, 200, 255, 0.5)';
    ctx.beginPath();
    ctx.arc(centerX, centerY, activeRadius + 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Draw circle background
  ctx.save();
  ctx.fillStyle = state.isOverZone
    ? 'rgba(100, 200, 255, 0.2)'
    : 'rgba(0, 0, 0, 0.3)';
  ctx.strokeStyle = state.isOverZone
    ? 'rgba(100, 200, 255, 0.8)'
    : 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = state.isOverZone ? 4 : 2;

  ctx.beginPath();
  ctx.arc(centerX, centerY, activeRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Draw icon/text
  const iconText = state.isOverZone ? '↓' : '⊞';
  const iconFontSize = scaleFontSize(state.isOverZone ? 48 : 36);
  
  drawText(
    ctx,
    iconText,
    centerX,
    centerY - 20,
    {
      fontSize: iconFontSize,
      fontWeight: '600',
      color: state.isOverZone
        ? 'rgba(100, 200, 255, 0.9)'
        : 'rgba(255, 255, 255, 0.7)',
      textAlign: 'center',
      textBaseline: 'middle',
      fontFamily: '"Manrope", sans-serif'
    }
  );

  // Draw label
  const labelText = state.isOverZone ? 'Drop here' : 'Drag files or cards here';
  const labelFontSize = scaleFontSize(14);
  
  drawText(
    ctx,
    labelText,
    centerX,
    centerY + 30,
    {
      fontSize: labelFontSize,
      fontWeight: '500',
      color: state.isOverZone
        ? 'rgba(100, 200, 255, 0.9)'
        : 'rgba(255, 255, 255, 0.6)',
      textAlign: 'center',
      textBaseline: 'middle',
      fontFamily: '"Manrope", sans-serif'
    }
  );

  // Draw count badges if items are in context
  if (state.fileCount > 0 || state.cardCount > 0) {
    const totalCount = state.fileCount + state.cardCount;
    const badgeText = String(totalCount);
    const badgeFontSize = scaleFontSize(12);
    const badgeX = centerX + activeRadius - 20;
    const badgeY = centerY - activeRadius + 20;

    // Draw badge background
    ctx.save();
    ctx.fillStyle = 'rgba(100, 200, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(badgeX, badgeY, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw badge text
    drawText(
      ctx,
      badgeText,
      badgeX,
      badgeY,
      {
        fontSize: badgeFontSize,
        fontWeight: '700',
        color: 'rgba(0, 0, 0, 0.9)',
        textAlign: 'center',
        textBaseline: 'middle',
        fontFamily: '"Manrope", sans-serif'
      }
    );
  }
}

/**
 * Check if point is inside drag drop zone
 */
export function isPointInDragDropZone(
  x: number,
  y: number,
  gridState: GridState
): boolean {
  const centerX = gridState.width / 2;
  const centerY = gridState.height / 2;
  const radius = 120;
  const distance = Math.sqrt(
    Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
  );
  return distance <= radius;
}




